import { useNavigate } from "react-router";
import { Search, ChevronDown } from "lucide-react";
import { t } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import type {
  ContributionRequest,
  ContributionRequestDirection,
  ContributionRequestStatus,
} from "@/types/contribution";

const TYPE_ICONS: Record<string, string> = {
  walk_initiative: "directions_walk",
  juthoor_initiative: "park",
};

const TYPE_ICON_STYLES: Record<string, string> = {
  walk_initiative: "bg-brand-blue/10 text-brand-blue",
  juthoor_initiative: "bg-brand-green/10 text-brand-green",
};

const TYPE_LABEL_KEYS: Record<string, "typeWalk" | "typeJuthoor"> = {
  walk_initiative: "typeWalk",
  juthoor_initiative: "typeJuthoor",
};

interface ContributionRequestsTableProps {
  requests: ContributionRequest[];
  direction: ContributionRequestDirection;
  onDirectionChange: (d: ContributionRequestDirection) => void;
  onSearch: (s: string) => void;
  onStatusFilter: (s: ContributionRequestStatus | undefined) => void;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
  loading: boolean;
  actionLoading: boolean;
}

function StatusBadge({ status }: { status: ContributionRequestStatus }) {
  const { locale } = useAppContext();
  const styles: Record<ContributionRequestStatus, string> = {
    pending: "bg-warning-light text-warning-dark dark:bg-warning/40 dark:text-warning",
    accepted: "bg-success-light text-success-dark dark:bg-success/40 dark:text-success",
    rejected: "bg-error-light text-error-dark dark:bg-error/40 dark:text-error",
  };
  const labels: Record<ContributionRequestStatus, string> = {
    pending: t(locale, "pending"),
    accepted: t(locale, "statusAccepted"),
    rejected: t(locale, "statusRejected"),
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export function ContributionRequestsTable({
  requests,
  direction,
  onDirectionChange,
  onSearch,
  onStatusFilter,
  onAccept,
  onReject,
  loading,
  actionLoading,
}: ContributionRequestsTableProps) {
  const { locale } = useAppContext();
  const navigate = useNavigate();

  return (
    <section className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold">{t(locale, "contributionRequests")}</h3>
          <p className="text-sm text-foreground-secondary mt-1">
            {t(locale, "trackRequests")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground w-[18px] h-[18px] pointer-events-none"
              strokeWidth={1.8}
              aria-hidden="true"
            />
            <input
              type="text"
              className="pe-10 ps-4 py-2 bg-muted border-none rounded-lg text-sm focus:ring-2 focus:ring-primary/20 w-full md:w-64"
              placeholder={t(locale, "searchByName")}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          {/* Status filter */}
          <div className="relative">
            <select
              className="bg-muted border-none rounded-lg text-sm py-2 ps-4 pe-10 focus:ring-2 focus:ring-primary/20 appearance-none bg-none"
              onChange={(e) =>
                onStatusFilter(
                  (e.target.value as ContributionRequestStatus) || undefined
                )
              }
              defaultValue=""
            >
              <option value="">{t(locale, "selectStatusFilter")}</option>
              <option value="pending">{t(locale, "pending")}</option>
              <option value="accepted">{t(locale, "statusAccepted")}</option>
              <option value="rejected">{t(locale, "statusRejected")}</option>
            </select>
            <ChevronDown
              className="absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400 w-[18px] h-[18px]"
              aria-hidden="true"
            />
          </div>
          {/* Direction toggle */}
          <div className="flex rounded-lg overflow-hidden border border-border">
            <button
              type="button"
              onClick={() => onDirectionChange("received")}
              className={`px-4 py-2 text-sm transition-colors ${
                direction === "received"
                  ? "bg-primary text-white font-medium"
                  : "bg-muted hover:bg-muted"
              }`}
            >
              {t(locale, "receivedDir")}
            </button>
            <button
              type="button"
              onClick={() => onDirectionChange("sent")}
              className={`px-4 py-2 text-sm transition-colors ${
                direction === "sent"
                  ? "bg-primary text-white font-medium"
                  : "bg-muted hover:bg-muted"
              }`}
            >
              {t(locale, "sentDir")}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-start border-collapse">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "type")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "title")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "status")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "requestDate")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "requestedBy")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-20 text-center">
                  <span className="material-icons-outlined text-4xl text-primary animate-spin">
                    refresh
                  </span>
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-20 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                      <span className="material-icons-outlined text-4xl text-muted-foreground">
                        inbox
                      </span>
                    </div>
                    <p className="text-foreground-secondary font-medium">
                      {t(locale, "noContributionRequests")}
                    </p>
                    <p className="text-muted-foreground text-sm mt-1">
                      {t(locale, "noContributionRequestsDesc")}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              requests.map((req) => (
                <tr
                  key={req.id}
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => req.initiative_id && navigate(`/dashboard/initiatives/${req.initiative_id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          TYPE_ICON_STYLES[req.initiative?.type ?? ""] ?? "bg-muted text-muted-foreground"
                        }`}
                      >
                        <span className="material-icons-outlined text-sm">
                          {TYPE_ICONS[req.initiative?.type ?? ""] ?? "eco"}
                        </span>
                      </div>
                      <span className="text-sm font-medium">
                        {req.initiative?.type ? t(locale, TYPE_LABEL_KEYS[req.initiative.type] ?? "typeWalk") : "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold">{req.initiative?.title ?? "-"}</td>
                  <td className="px-6 py-4">
                    <StatusBadge status={req.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground-secondary">
                    {req.created_at}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground-secondary">
                    {(direction === "received" ? req.inviter?.name : req.enabler?.name) ?? "-"}
                  </td>
                  <td className="px-6 py-4">
                    {direction === "received" && req.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={() => onAccept(req.id)}
                          className="px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          {t(locale, "acceptAction")}
                        </button>
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={() => onReject(req.id)}
                          className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                        >
                          {t(locale, "rejectAction")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
